/**
 * =============================================================================
 * HEADER MOBILE FIXES JS - topriding.com
 * 处理 Sticky Header 滚动逻辑与移动端菜单交互
 * =============================================================================
 */

(function() {
  'use strict';

  // =========================================
  // 配置项
  // =========================================
  const CONFIG = {
    stickyThreshold: 50,           // 触发 sticky 的滚动阈值
    scrollThreshold: 10,           // 判断滚动方向的最小距离
    debounceDelay: 10,             // 防抖延迟 (毫秒)
    stickyClass: 'is-header--stuck',
    scrollDownClass: 'is-scrolling-down',
    scrollUpClass: 'is-scrolling-up',
    menuOpenClass: 'menu-open',
    stickyReadyClass: 't4s-hsticky__ready'
  };

  // =========================================
  // 状态管理
  // =========================================
  let lastScrollTop = 0;
  let isScrolling = false;
  let ticking = false;
  let headerOptions = {
    isSticky: true,
    isTransparent: false,
    hideScroldown: false
  };

  // =========================================
  // DOM 元素缓存
  // =========================================
  const elements = {
    html: document.documentElement,
    body: document.body,
    header: null,
    headerWrapper: null,
    menuDrawer: null,
    announcementBar: null,
    topBar: null
  };

  // =========================================
  // 工具函数
  // =========================================

  /**
   * 防抖函数
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * 获取元素
   */
  function cacheElements() {
    elements.header = document.querySelector('.t4s-section-header');
    elements.headerWrapper = document.querySelector('.t4s-header__wrapper');
    elements.menuDrawer = document.querySelector('#t4s-menu-drawer');
    elements.announcementBar = document.querySelector('#shopify-section-announcement-bar');
    elements.topBar = document.querySelector('#shopify-section-top-bar');

    // 读取 Header 配置
    if (elements.headerWrapper) {
      const optionsAttr = elements.headerWrapper.getAttribute('data-header-options');
      if (optionsAttr) {
        try {
          headerOptions = JSON.parse(optionsAttr);
        } catch (e) {
          console.warn('Failed to parse header options:', e);
        }
      }
    }
  }

  /**
   * 计算公告栏和 Topbar 的总高度
   */
  function getTopBarsHeight() {
    let height = 0;
    
    if (elements.announcementBar && elements.announcementBar.getAttribute('aria-hidden') !== 'true') {
      height += elements.announcementBar.offsetHeight || 0;
    }
    
    if (elements.topBar) {
      height += elements.topBar.offsetHeight || 0;
    }
    
    return height;
  }

  // =========================================
  // Sticky Header 逻辑
  // =========================================

  /**
   * 更新 Header Sticky 状态
   */
  function updateHeaderState() {
    if (!elements.header || !headerOptions.isSticky) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const topBarsHeight = getTopBarsHeight();
    const threshold = CONFIG.stickyThreshold + topBarsHeight;

    // 判断是否应该 Sticky
    if (scrollTop > threshold) {
      elements.html.classList.add(CONFIG.stickyClass);
      
      // 如果启用了"滚动时隐藏"
      if (headerOptions.hideScroldown) {
        handleScrollDirection(scrollTop);
      }
    } else {
      elements.html.classList.remove(CONFIG.stickyClass);
      elements.html.classList.remove(CONFIG.scrollDownClass);
      elements.html.classList.remove(CONFIG.scrollUpClass);
    }

    lastScrollTop = scrollTop;
  }

  /**
   * 处理滚动方向
   */
  function handleScrollDirection(scrollTop) {
    if (Math.abs(scrollTop - lastScrollTop) < CONFIG.scrollThreshold) {
      return;
    }

    if (scrollTop > lastScrollTop && scrollTop > 100) {
      // 向下滚动
      elements.html.classList.add(CONFIG.scrollDownClass);
      elements.html.classList.remove(CONFIG.scrollUpClass);
    } else {
      // 向上滚动
      elements.html.classList.remove(CONFIG.scrollDownClass);
      elements.html.classList.add(CONFIG.scrollUpClass);
    }
  }

  /**
   * 滚动事件处理 (使用 requestAnimationFrame 优化)
   */
  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        updateHeaderState();
        ticking = false;
      });
      ticking = true;
    }
  }

  /**
   * 初始化 Sticky Header
   */
  function initStickyHeader() {
    if (!elements.header || !headerOptions.isSticky) return;

    // 标记 Sticky 已就绪
    elements.html.classList.add(CONFIG.stickyReadyClass);

    // 初始检查
    updateHeaderState();

    // 监听滚动事件 (使用 passive 优化性能)
    window.addEventListener('scroll', onScroll, { passive: true });

    // iOS Safari 专属：监听 touchmove
    if (isIOS()) {
      document.addEventListener('touchmove', onScroll, { passive: true });
    }
  }

  // =========================================
  // 移动端菜单交互修复
  // =========================================

  /**
   * 处理菜单打开/关闭
   */
  function handleMenuToggle(isOpen) {
    if (isOpen) {
      elements.html.classList.add(CONFIG.menuOpenClass);
      elements.body.classList.add(CONFIG.menuOpenClass);
      
      // 阻止背景滚动
      disableBodyScroll();
    } else {
      elements.html.classList.remove(CONFIG.menuOpenClass);
      elements.body.classList.remove(CONFIG.menuOpenClass);
      
      // 恢复背景滚动
      enableBodyScroll();
    }
  }

  /**
   * 阻止 body 滚动
   */
  function disableBodyScroll() {
    const scrollY = window.scrollY;
    elements.body.style.position = 'fixed';
    elements.body.style.top = `-${scrollY}px`;
    elements.body.style.width = '100%';
  }

  /**
   * 恢复 body 滚动
   */
  function enableBodyScroll() {
    const scrollY = elements.body.style.top;
    elements.body.style.position = '';
    elements.body.style.top = '';
    elements.body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  }

  /**
   * 监听菜单状态变化
   */
  function observeMenuDrawer() {
    if (!elements.menuDrawer) return;

    // 使用 MutationObserver 监听 aria-hidden 变化
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'aria-hidden') {
          const isHidden = elements.menuDrawer.getAttribute('aria-hidden') === 'true';
          handleMenuToggle(!isHidden);
        }
      });
    });

    observer.observe(elements.menuDrawer, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }

  /**
   * 增强菜单按钮点击
   */
  function enhanceMenuButton() {
    const menuBtn = document.querySelector('[data-menu-drawer]');
    if (!menuBtn) return;

    // 确保按钮可点击
    menuBtn.style.pointerEvents = 'auto';
    menuBtn.style.cursor = 'pointer';
    
    // 增加触摸区域
    menuBtn.style.minHeight = '44px';
    menuBtn.style.minWidth = '44px';

    // 添加点击反馈
    menuBtn.addEventListener('touchstart', function() {
      this.style.opacity = '0.7';
    }, { passive: true });

    menuBtn.addEventListener('touchend', function() {
      this.style.opacity = '1';
    }, { passive: true });

    // 防止意外双击
    let lastTap = 0;
    menuBtn.addEventListener('touchend', function(e) {
      const currentTime = new Date().getTime();
      const tapLength = currentTime - lastTap;
      
      if (tapLength < 300 && tapLength > 0) {
        e.preventDefault();
      }
      lastTap = currentTime;
    });
  }

  // =========================================
  // iOS Safari 专属修复
  // =========================================

  /**
   * 检测是否为 iOS 设备
   */
  function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  /**
   * iOS Safari 首次加载白底修复
   */
  function fixIOSInitialLoad() {
    if (!isIOS()) return;

    // 强制触发重绘
    if (elements.headerWrapper) {
      elements.headerWrapper.style.transform = 'translateZ(0)';
      
      // 延迟应用 backdrop-filter
      setTimeout(() => {
        elements.headerWrapper.style.webkitBackdropFilter = 'blur(10px)';
        elements.headerWrapper.style.backdropFilter = 'blur(10px)';
      }, 100);
    }
  }

  /**
   * iOS Safari 滚动优化
   */
  function optimizeIOSScroll() {
    if (!isIOS() || !elements.header) return;

    // 添加 -webkit-overflow-scrolling
    elements.header.style.webkitOverflowScrolling = 'touch';
    
    // 强制 GPU 加速
    elements.header.style.transform = 'translate3d(0, 0, 0)';
    elements.header.style.webkitTransform = 'translate3d(0, 0, 0)';
  }

  // =========================================
  // 性能优化
  // =========================================

  /**
   * 清理旧的滚动事件监听器
   */
  function cleanupOldListeners() {
    // 移除可能存在的旧的 window.onscroll
    if (window.onscroll) {
      window.onscroll = null;
    }

    // 清理可能冲突的滚动监听
    const oldListeners = window.__headerScrollListeners || [];
    oldListeners.forEach(listener => {
      window.removeEventListener('scroll', listener);
    });
    window.__headerScrollListeners = [];
  }

  /**
   * Intersection Observer 优化 (可选)
   */
  function initIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    const sentinel = document.querySelector('#t4s-hsticky__sentinel');
    if (!sentinel || !elements.header) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          elements.html.classList.add(CONFIG.stickyClass);
        } else {
          elements.html.classList.remove(CONFIG.stickyClass);
        }
      },
      {
        threshold: [0],
        rootMargin: '0px 0px 0px 0px'
      }
    );

    observer.observe(sentinel);
  }

  // =========================================
  // 窗口大小调整处理
  // =========================================

  /**
   * 响应窗口大小变化
   */
  const handleResize = debounce(() => {
    // 重新计算高度
    updateHeaderState();
    
    // iOS 地址栏隐藏时重新计算
    if (isIOS()) {
      setTimeout(updateHeaderState, 300);
    }
  }, CONFIG.debounceDelay);

  // =========================================
  // 初始化
  // =========================================

  /**
   * 主初始化函数
   */
  function init() {
    // 等待 DOM 完全加载
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // 缓存元素
    cacheElements();

    // 清理旧监听器
    cleanupOldListeners();

    // 初始化 Sticky Header
    initStickyHeader();

    // 初始化移动菜单
    observeMenuDrawer();
    enhanceMenuButton();

    // iOS 专属修复
    if (isIOS()) {
      fixIOSInitialLoad();
      optimizeIOSScroll();
    }

    // 监听窗口大小变化
    window.addEventListener('resize', handleResize, { passive: true });

    // 页面可见性变化时重新检查
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        updateHeaderState();
      }
    });

    console.log('✅ Header mobile fixes initialized');
  }

  // =========================================
  // 暴露到全局 (便于调试)
  // =========================================
  window.HeaderMobileFixes = {
    init: init,
    updateState: updateHeaderState,
    config: CONFIG,
    elements: elements
  };

  // =========================================
  // 自动初始化
  // =========================================
  init();

})();

